import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import RarityBadge from '@/components/RarityBadge';
import SaleDetailsModal from '@/components/SaleDetailsModal';
import OfficialTag from '@/components/OfficialTag';

import { SALE_STATUS, getSaleStatusTranslationKey } from '@/config/saleStatus';
import { formatDate, formatCurrency } from '@/utils/formatUtils';
import { calculateCountdown } from '@/utils/countdownUtils';
import warningIcon from '@/assets/warning_purple.svg';
import warningPenaltyIcon from '@/assets/warning_red.svg';
import { openPenaltyPaymentModal } from '@/stores/penaltyPaymentStore';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const SaleCard = ({ sale, onWithdrawClick, onStatusClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [openWithShipping, setOpenWithShipping] = useState(false);
  const penalty = sale.penalty;
  const hasOutstandingPenalty = penalty?.status === 'OUTSTANDING';

  const getGrade = () => {
    if (sale.listing.psa) {
      return sale.listing.psa.replace('PSA_', 'PSA ');
    }
    if (sale.listing.bundledQuantity) {
      const unit = sale.listing.product?.boxType === 'PACK' ? t('box.pack') : t('box.box_unit');
      return `${sale.listing.bundledQuantity} ${unit}`;
    }
    if (sale.listing.condition) {
      return t(`sell.product_info.conditions.${sale.listing.condition.toLowerCase()}`);
    }
    return '-';
  };

  // Helper to get a displayable status text
  const getStatusText = (status, isOfficial) => {
    return t(getSaleStatusTranslationKey(status, isOfficial));
  };

  const handleOpenDetailsModal = () => {
    setOpenWithShipping(false);
    setIsDetailsModalOpen(true);
  };

  const handleOpenShippingModal = () => {
    setOpenWithShipping(true);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setOpenWithShipping(false);
  };

  const handleCardNameClick = () => {
    if (sale.listing.type === 'NON_PSA_SINGLE_CARD') {
      navigate(`/listing/${sale.listing.id}`);
    } else {
      navigate(`/card/${sale.listing.product.id}`);
    }
  };

  const handleWithdrawClick = () => {
    if (onWithdrawClick) {
      onWithdrawClick();
    }
  };

  const handlePayPenalty = () => {
    openPenaltyPaymentModal();
  };

  const handleStatusClick = () => {
    if (onStatusClick) {
      onStatusClick(sale.id);
    }
  };

  // Calculate countdown for PAID status
  const countdownInfo = useMemo(() => {
    return calculateCountdown(sale, t);
  }, [sale, t]);

  return (
    <>
      <div className="py-6">
        <div className="grid grid-cols-[1fr,108px,108px,108px,180px] gap-6">
          <div className="w-full flex justify-start items-center gap-4">
            <div className="w-[100px] h-[100px] relative bg-white rounded overflow-hidden">
              <img
                src={getProductThumbnailUrl(sale.listing.product.images?.[0]?.url)}
                alt={sale.listing.product.localizedName}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1 inline-flex flex-col justify-start items-start">
              <div>
                <div className="min-w-0">
                  {sale.listing.isOfficial && (
                    <OfficialTag className="align-middle mr-1.5" />
                  )}
                  <span
                    className="inline align-middle text-[#111111] text-sm font-normal hover:underline cursor-pointer whitespace-normal break-words min-w-0"
                    onClick={handleCardNameClick}
                  >
                    {sale.listing.product.localizedRawName || sale.listing.product.localizedName}
                    {sale.listing.product.rarityInfo?.sub?.name ? `(${sale.listing.product.rarityInfo.sub.name})` : ''}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {sale.listing.product.rarityInfo?.main && (
                  <RarityBadge rarity={sale.listing.product.rarityInfo.main} />
                )}
                {(sale.listing.product?.set?.code || (sale.listing.product?.rawCode && sale.listing.product?.type !== 'CARD_BOX')) && (
                  <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                    <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                      {sale.listing.product?.type === 'CARD_BOX' ? sale.listing.product?.set?.code : (sale.listing.product?.set?.code && sale.listing.product?.rawCode ? `${sale.listing.product.set.code} ${sale.listing.product.rawCode}` : sale.listing.product?.set?.code || sale.listing.product?.rawCode)}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-col justify-center items-start gap-1">
              <div className="text-[#aaaaaa] text-xs font-normal">
                {sale.status === SALE_STATUS.COMPLETED ? (
                  <>
                    {t('common.finishDate')} {formatDate(sale.finishedAt)}
                  </>
                ) : (
                  <>
                    {t('common.orderDate')} {formatDate(sale.createdAt)}
                  </>
                )}
              </div>
              <div className="text-[#aaaaaa] text-xs font-normal">
                {t('common.orderNumber')} {sale.number}
              </div>
            </div>
            </div>
            
          </div>
          <div className="flex justify-center items-center text-[#666666] text-sm font-normal">
            {getGrade()}
          </div>
          <div className="flex justify-center items-center text-center text-[#666666] text-sm font-normal">
            {formatCurrency(sale.monetary.price)}
          </div>
          <div
            className="flex justify-center items-center text-center text-[#666666] text-sm font-normal cursor-pointer hover:text-[#3b5493] hover:underline"
            onClick={handleStatusClick}
          >
            {getStatusText(sale.status, sale.listing?.isOfficial)}
          </div>
          <div className="inline-flex flex-col justify-center items-center gap-1.5">
            {hasOutstandingPenalty ? (
              <Button
                variant="danger"
                size="s"
                className="w-24 mb-1 px-0"
                onClick={handlePayPenalty}
              >
                {t('sales.penalty.card.payAction')}
              </Button>
            ) : (
              <>
                {sale.status === SALE_STATUS.PAID && (
                  <Button
                    variant="primary"
                    size="s"
                    className="w-24 mb-1 px-0"
                    onClick={handleOpenShippingModal}
                  >
                    {t('sales.countdown.shipNow')}
                  </Button>
                )}
                {sale.status === SALE_STATUS.COMPLETED && (
                  <Button
                    variant="default"
                    size="s"
                    className="w-24 mb-1 px-0"
                    onClick={handleWithdrawClick}
                  >
                    {t('sales.completed.withdrawButton')}
                  </Button>
                )}
              </>
            )}
            <Button
              variant="default"
              size="s"
              className="w-24"
              onClick={handleOpenDetailsModal}
            >
              {t('common.details')}
            </Button>
          </div>
        </div>

        {hasOutstandingPenalty && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-md">
            <img src={warningPenaltyIcon} alt="warning" className="w-4 h-4" />
            <div className="flex flex-col text-[#ff2b00]">
              <span className="text-sm leading-[21px]">
                {t('sales.penalty.card.restriction')}
              </span>
            </div>
          </div>
        )}

        {sale.status === SALE_STATUS.PAID && countdownInfo && (
          <div className="mt-4 h-7 flex justify-start items-center gap-2 px-3">
            <img src={warningIcon} alt='warning' className='w-4 h-4' />
            <div className="justify-center text-[#3b5493] text-sm font-normal leading-[21px]">
              {t('sales.messages.shipping_alert', { time: countdownInfo.timeLeft, deadline: countdownInfo.deadline })}
            </div>
          </div>
        )}
      </div>

      {/* Sale Details Modal */}
      {isDetailsModalOpen && (
        <SaleDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          sale={sale}
          openShippingPanel={openWithShipping}
          onWithdrawClick={onWithdrawClick}
        />
      )}


    </>
  );
};

export default SaleCard; 
